import React, { useState } from 'react';
import { Folder, ChevronRight, ChevronDown, Plus, MoreVertical, Edit2, Trash2, Move } from 'lucide-react';
import { useFolders } from '../../contexts/FolderContext';
import { Folder as FolderType } from '../../types/shared';

interface FolderTreeProps {
  onFolderSelect?: (folder: FolderType | null) => void;
  selectedFolderId?: string | null;
  showActions?: boolean;
  className?: string;
}

interface FolderNodeProps {
  folder: FolderType & { children?: FolderType[] };
  level: number;
  onFolderSelect?: (folder: FolderType | null) => void;
  selectedFolderId?: string | null;
  showActions?: boolean;
}

const FolderNode: React.FC<FolderNodeProps> = ({
  folder,
  level,
  onFolderSelect,
  selectedFolderId,
  showActions = true
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(folder.name);
  
  const { updateFolder, deleteFolder, createFolder } = useFolders();
  
  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = selectedFolderId === folder._id;
  
  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
    onFolderSelect?.(folder);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowContextMenu(true);
  };

  const handleEdit = async () => {
    if (editName.trim() && editName !== folder.name) {
      try {
        await updateFolder(folder._id, { name: editName.trim() });
        setIsEditing(false);
      } catch (error) {
        console.error('Error updating folder:', error);
      }
    } else {
      setIsEditing(false);
      setEditName(folder.name);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar la carpeta "${folder.name}"?`)) {
      try {
        await deleteFolder(folder._id);
      } catch (error) {
        console.error('Error deleting folder:', error);
      }
    }
    setShowContextMenu(false);
  };

  const handleCreateSubfolder = async () => {
    const name = prompt('Nombre de la nueva subcarpeta:');
    if (name?.trim()) {
      try {
        await createFolder(name.trim(), folder._id);
      } catch (error) {
        console.error('Error creating subfolder:', error);
      }
    }
    setShowContextMenu(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEdit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditName(folder.name);
    }
  };

  return (
    <div className="select-none">
      <div
        className={`
          flex items-center py-2 px-3 rounded-md cursor-pointer group hover:bg-gray-700 transition-all duration-200 relative
          ${isSelected 
            ? 'bg-blue-500/20 text-blue-400 border-l-4 border-blue-400 shadow-md' 
            : 'text-gray-300 hover:text-white'
          }
        `}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        {/* Expand/Collapse Icon */}
        <div className="w-4 h-4 mr-1 flex items-center justify-center">
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )
          ) : (
            <div className="w-3 h-3" />
          )}
        </div>

        {/* Folder Icon */}
        <div 
          className="w-4 h-4 mr-2 flex items-center justify-center"
          style={{ color: folder.color }}
        >
          <Folder className="w-4 h-4" />
        </div>

        {/* Folder Name */}
        <div className="flex-1 min-w-0 flex items-center">
          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleEdit}
              onKeyDown={handleKeyDown}
              className="w-full px-1 py-0.5 text-sm bg-gray-700 border border-gray-600 text-white rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          ) : (
            <>
              <span className="text-sm font-medium truncate">{folder.name}</span>
              {isSelected && (
                <div className="ml-2 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              )}
            </>
          )}
        </div>

        {/* Ads Count */}
        <span className="text-xs text-gray-500 ml-2">
          {folder.adsCount}
        </span>

        {/* Actions */}
        {showActions && (
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              className="p-1 hover:bg-stone-700 rounded"
              title="Renombrar carpeta"
            >
              <Edit2 className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowContextMenu(true);
              }}
              className="p-1 hover:bg-stone-700 rounded"
              title="Más opciones"
            >
              <MoreVertical className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {showContextMenu && (
        <div className="fixed inset-0 z-50" onClick={() => setShowContextMenu(false)}>
          <div className="absolute bg-stone-900 border border-stone-700 rounded-md shadow-gum-lg py-1 min-w-32">
            <button
              onClick={handleCreateSubfolder}
              className="w-full px-3 py-2 text-left text-sm text-stone-200 hover:bg-stone-800 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva subcarpeta
            </button>
            <button
              onClick={() => {
                setIsEditing(true);
                setShowContextMenu(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-stone-200 hover:bg-stone-800 flex items-center"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Renombrar
            </button>
            <button
              onClick={handleDelete}
              className="w-full px-3 py-2 text-left text-sm hover:bg-red-950/40 text-red-400 flex items-center"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </button>
          </div>
        </div>
      )}

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {folder.children!.map((child) => (
            <FolderNode
              key={child._id}
              folder={child}
              level={level + 1}
              onFolderSelect={onFolderSelect}
              selectedFolderId={selectedFolderId}
              showActions={showActions}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const FolderTree: React.FC<FolderTreeProps> = ({
  onFolderSelect,
  selectedFolderId,
  showActions = true,
  className = ''
}) => {
  const { foldersTree, isLoading, error, createFolder } = useFolders();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      try {
        await createFolder(newFolderName.trim());
        setNewFolderName('');
        setShowCreateForm(false);
      } catch (error) {
        console.error('Error creating folder:', error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreateFolder();
    } else if (e.key === 'Escape') {
      setShowCreateForm(false);
      setNewFolderName('');
    }
  };

  if (isLoading) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-stone-700 rounded mb-2"></div>
          <div className="h-4 bg-stone-700 rounded mb-2"></div>
          <div className="h-4 bg-stone-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 text-red-600 ${className}`}>
        <p className="text-sm">Error al cargar carpetas: {error}</p>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <h3 className="text-sm font-semibold text-gray-300">Carpetas</h3>
        {showActions && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
            title="Nueva carpeta"
          >
            <Plus className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Create Folder Form */}
      {showCreateForm && (
        <div className="p-3 border-b border-gray-700 bg-gray-800/50">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nombre de la carpeta"
            className="w-full px-2 py-1 text-sm bg-gray-700 border border-gray-600 text-white placeholder-gray-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            autoFocus
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleCreateFolder}
              className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Crear
            </button>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setNewFolderName('');
              }}
              className="px-3 py-1 text-xs bg-gray-600 text-gray-300 rounded hover:bg-gray-500 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Root Folders */}
      <div className="p-2">
        {/* "TODOS" option */}
        <div
          className={`
            flex items-center py-2 px-3 rounded-md cursor-pointer hover:bg-gray-700 transition-all duration-200 relative
            ${selectedFolderId === null
              ? 'bg-blue-500/20 text-blue-400 border-l-4 border-blue-400 shadow-md'
              : 'text-gray-300 hover:text-white'
            }
          `}
          onClick={() => onFolderSelect?.(null)}
        >
          <div className="w-4 h-4 mr-2 flex items-center justify-center">
            <Folder className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0 flex items-center">
            <span className="text-sm font-medium">TODOS</span>
            {selectedFolderId === null && (
              <div className="ml-2 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
            )}
          </div>
        </div>

        {/* Folder Tree */}
        {foldersTree.map((folder) => (
          <FolderNode
            key={folder._id}
            folder={folder}
            level={0}
            onFolderSelect={onFolderSelect}
            selectedFolderId={selectedFolderId}
            showActions={showActions}
          />
        ))}

        {foldersTree.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay carpetas creadas</p>
            <p className="text-xs">Crea tu primera carpeta para organizar tus anuncios</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderTree;

